import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Flex, List, ListItem } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { L3_PATHS } from 'Deer/constants/routes';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import { ColDef, ICellRendererParams, ITooltipParams, RowClickedEvent } from 'ag-grid-community';
import { format, parseISO } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { REVIEW_CYCLE_STEPS_V1 } from 'reducers/reviewCreationSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import {
  checkDemoReviewCreationTriggeredReviewsEnabled,
  checkNewNavEnabled,
  checkSimplifiedReviewCreationEnabled,
} from 'utils/helpers/featureFlags';

import { Plus } from 'assets/assetsV1/iconComponents';
import addReviewV1 from 'assets/assetsV1/illustrations/newIllustrations/addReviewV1.svg';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';
import { routes } from 'constants/routes';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Button } from 'uiToolkitV1/Button';
import { ChipSelect } from 'uiToolkitV1/ChipSelect';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  REVIEW_CYCLE_STATUS_COLORS,
  ReviewCycleRow,
  useArchiveUnArchiveReviewCycleMutation,
  useGetReviewCycles,
} from '../Hooks/dataHooks/reviewsListDataHooks';
import { generateReviewListOptions } from '../utils';

const StyledListItem = styled(ListItemComp)`
  max-width: 250px;
  p {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: no-wrap;

    &:first-child {
      height: fit-content !important;
    }
  }
`;

const ReviewCycleRenderer: React.FC<ICellRendererParams<ReviewCycleRow>> = ({ data }) => {
  const startDate = data.reviewCycle.startDate ? format(parseISO(data.reviewCycle.startDate), 'MMM dd, yyyy') : '';
  const endDate = data.reviewCycle.endDate ? format(parseISO(data.reviewCycle.endDate), 'MMM dd, yyyy') : '';
  const reviewPeriod = data.reviewCycle.startDate && data.reviewCycle.endDate ? `${startDate} to ${endDate}` : '';

  return (
    <ListItemComp
      title={data.reviewCycle.name}
      subTitle={reviewPeriod}
      withoutBackground
      LeftComponent={null}
      RightComponent={null}
      isDisabled={false}
      subSubtitle={null}
      onClick={() => {}}
      active={false}
      isBoldText={false}
    />
  );
};

const ViewsTooltipRenderer: React.FC<ITooltipParams<ReviewCycleRow>> = ({ data }) => (
  <List bg="neutral.595E6A" borderRadius="4px" p={3} color="white">
    {data.cohorts.map((c) => (
      <ListItem>{c?.name ?? ''}</ListItem>
    ))}
  </List>
);

const ViewsRenderer: React.FC<ICellRendererParams<ReviewCycleRow>> = ({ data }) => {
  // select only three views to display
  const views = data.cohorts?.slice(0, 2) ?? [];

  const remainingViewsCount = (data.cohorts?.length ?? 0) - views.length;

  const remainingViewsCountComponent =
    remainingViewsCount > 0 ? <TextBodyMedium bold>+{remainingViewsCount}</TextBodyMedium> : null;

  return (
    <StyledListItem
      title={views.map((v) => v?.name ?? '').join(', ')}
      subTitle={data.reviewCycle.hasCustomSettings ? 'Customized settings across' : null}
      withoutBackground
      LeftComponent={null}
      RightComponent={remainingViewsCountComponent}
      isDisabled={false}
      subSubtitle={null}
      onClick={() => {}}
      active={false}
      isBoldText={false}
    />
  );
};

const StatusRenderer: React.FC<ICellRendererParams<ReviewCycleRow>> = ({ data }) => (
  <TagColored color={REVIEW_CYCLE_STATUS_COLORS[data.reviewCycle.currentStage]}>{data.currentStageName}</TagColored>
);

const CycleTypeRenderer: React.FC<ICellRendererParams<ReviewCycleRow>> = ({ data }) => {
  const reviewCycleId = data.reviewCycle.id;
  switch (reviewCycleId) {
    case 1104:
      return <TagColored color="grey">Recurring</TagColored>;
    case 1103:
      return <TagColored color="grey">Continuous</TagColored>;

    default:
      return <TagColored color="grey">One time</TagColored>;
  }
};

const ROWS_PER_PAGE = 5;
const ROW_HEIGHT = 72;

export const ReviewsList = () => {
  const [isArchive, setIsArchive] = useState(false);
  const gridRef = useRef();
  const { data: activeCycles = [], isLoading: isLoadingActiveCycles } = useGetReviewCycles({
    isArchive: false,
  });
  const { data: archivedCycles = [], isLoading: isLoadingArchivedCycles } = useGetReviewCycles({
    isArchive: true,
  });
  const isLoading = isArchive ? isLoadingArchivedCycles : isLoadingActiveCycles;
  const rowData = isArchive ? archivedCycles : activeCycles;
  const history = useHistory();
  const { mutate: archiveUnArchiveReviewCycle } = useArchiveUnArchiveReviewCycleMutation();

  const reviewCycleCreationRoute = checkNewNavEnabled()
    ? staticRoutesWithCustomSidebar.reviewCycleCreation
    : routes.settings.review.reviewCycle;

  const gridStyles = useMemo(() => ({ width: '100%' }), []);

  const demoColumnDefs = useMemo(
    () => [
      {
        colId: 'review-cycle-type',
        width: 200,
        headerName: 'Cycle type',
        cellRenderer: CycleTypeRenderer,
        valueGetter: ({ data }) => data.reviewCycle.name,
      },
    ],
    []
  );

  const isDemoReviewCreationTriggeredReviewsEnabled = checkDemoReviewCreationTriggeredReviewsEnabled();

  const columnDefs = useMemo<ColDef<ReviewCycleRow>[]>(() => {
    const columns = [
      {
        colId: 'review-cycle-and-period',
        minWidth: 300,
        flex: 1,
        headerName: 'Review cycle and Review period',
        cellRenderer: ReviewCycleRenderer,
        valueGetter: ({ data }) => data.reviewCycle.name,
      },
      {
        colId: 'users',
        width: 300,
        headerName: 'Users',
        cellRenderer: ViewsRenderer,
        tooltipComponent: ViewsTooltipRenderer,
        tooltipField: 'cohorts',
        valueGetter: ({ data }) => {
          if (!data.cohorts) return '';
          return data?.cohorts.map((v) => v?.name ?? '').join(', ');
        },
      },
      {
        colId: 'last-updated',
        headerName: 'Last updated on',
        field: 'lastUpdated',
        filter: 'agDateColumnFilter',
        valueGetter: ({ data }) => parseISO(data.reviewCycle.updatedOn.split('T')[0]),
        valueFormatter: ({ value }) => format(value, 'MMM dd, yyyy'),
      },
      {
        colId: 'status',
        headerName: 'Status',
        cellRenderer: StatusRenderer,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.reviewCycle.currentStage,
      },
    ];
    if (isDemoReviewCreationTriggeredReviewsEnabled) {
      // @ts-expect-error : typescript expecting a tootltipComponent inside this column , but it is not required
      columns.splice(1, 0, demoColumnDefs[0]);
    }
    return columns;
  }, [demoColumnDefs, isDemoReviewCreationTriggeredReviewsEnabled]);

  const defaultColDef = useMemo<ColDef<ReviewCycleRow>>(
    () => ({
      headerComponent: CustomHeaderCell,
      headerComponentParams: { enableMenu: true, enableSorting: true },
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  const onRowClicked = useCallback(
    ({ data, event }: RowClickedEvent<ReviewCycleRow>) => {
      if (event.defaultPrevented) {
        return;
      }
      if (checkSimplifiedReviewCreationEnabled()) {
        history.push(L3_PATHS.company.review.reviewCreation.replace(':reviewId', `${data.reviewCycle.id}`));
        return;
      }
      history.push(
        reviewCycleCreationRoute
          .replace(':formId', String(data.reviewCycle.id))
          .replace(':step', data.reviewCycle.lastEnabledSection ?? REVIEW_CYCLE_STEPS_V1.NAME)
      );
    },
    [history, reviewCycleCreationRoute]
  );

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = null;
    const tabId = 'review_cycles';
    const uniqueId = isArchive ? 'ArchivedReviewsGrid' : 'ActiveReviewsGrid';

    return getPersistenceKey({ moduleName, viewId, tabId, uniqueId });
  }, [isArchive]);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const handleReviewCreationClick = () => {
    if (checkSimplifiedReviewCreationEnabled()) {
      history.push(L3_PATHS.company.review.reviewCreation.replace(':reviewId', 'presets'));
    }
  };

  return (
    <Box
      p={checkNewNavEnabled() ? 3 : '22px 24px'}
      m={checkNewNavEnabled() ? 0 : 4}
      borderRadius="8px"
      bgColor="common.white"
    >
      <Flex mb="12px" justifyContent="space-between">
        <AgGridSearchBar gridRef={gridRef} />
        <Box ml="auto" />

        <Button
          StartIcon={Plus}
          isLink={!checkSimplifiedReviewCreationEnabled()}
          link={reviewCycleCreationRoute.replace(':formId', 'new').replace(':step', REVIEW_CYCLE_STEPS_V1.NAME)}
          onClick={handleReviewCreationClick}
        >
          Create New Review Cycle
        </Button>
      </Flex>
      <Flex alignItems="center" gap={2} mb={5}>
        <TextBodyMedium color="text.g3" bold>
          Showing
        </TextBodyMedium>
        <ChipSelect
          options={[
            { id: 'active', text: `Active Cycles (${activeCycles.length})` },
            { id: 'archived', text: `Archived (${archivedCycles.length})` },
          ]}
          selectedIds={[isArchive ? 'archived' : 'active']}
          onChange={([id]) => setIsArchive(id === 'archived')}
        />
      </Flex>
      {!isLoading && !(activeCycles?.length || archivedCycles?.length) ? (
        <EmptyScreen
          heading="Uh-oh! It seems like there are no review cycles currently created. Start something here."
          illustrationSrc={addReviewV1}
          // eslint-disable-next-line react/no-unstable-nested-components
          CTAComp={() => (
            <Button
              StartIcon={Plus}
              isLink={!checkSimplifiedReviewCreationEnabled()}
              link={reviewCycleCreationRoute.replace(':formId', 'new').replace(':step', REVIEW_CYCLE_STEPS_V1.NAME)}
              onClick={handleReviewCreationClick}
            >
              Create New Review Cycle
            </Button>
          )}
        />
      ) : (
        <div style={gridStyles}>
          <AgGridTable
            // providing key as state persistence key will force this component to be recreated.
            key={gridStatePersistenceKey}
            gridStatePersistenceKey={gridStatePersistenceKey}
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={rowData}
            domLayout="autoHeight"
            pagination
            suppressPaginationPanel={
              rowData?.length < ROWS_PER_PAGE || rowData?.length === undefined || rowData?.length === 0
            }
            paginationPageSize={ROWS_PER_PAGE}
            rowHeight={ROW_HEIGHT}
            onRowClicked={onRowClicked}
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            sideBar={sideBar}
            kebabMenuIsRowSpecific
            kebabMenuOptionsGenerator={(data) => {
              const options = generateReviewListOptions({
                data,
                archiveUnArchiveReviewCycle,
                history,
                isArchive,
              });
              return options;
            }}
            containerAutoHeight
          />
        </div>
      )}
    </Box>
  );
};
